import { useState } from "react";
import { DocumentList } from "./document-list";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { documentService } from "@/data/mock-documents";
import { containerService } from "@/data/mock-containers";
import { Modal, ModalOverlay, Dialog } from "@/components/application/modals/modal";
import { Select } from "@/components/base/select/select";
import type { UnmappedFile, DocumentFile, ViewMode } from "@/types/documents";
import { useTranslation } from "react-i18next";

interface UnmappedFilesListProps {
    files: UnmappedFile[];
    viewMode?: ViewMode;
    onFileAssociate?: (file: DocumentFile) => void;
    className?: string;
}

export const UnmappedFilesList = ({
    files,
    viewMode = "card",
    onFileAssociate,
    className,
}: UnmappedFilesListProps) => {
    const { t } = useTranslation("pages:documents");
    const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
    const [showAssociateModal, setShowAssociateModal] = useState(false);
    const [selectedContainerId, setSelectedContainerId] = useState("");

    const containerOptions = containerService
        .getAll()
        .map(c => ({ id: c.id, label: c.containerNumber }));

    const handleSelectAll = () => {
        if (selectedFiles.size === files.length) {
            setSelectedFiles(new Set());
        } else {
            setSelectedFiles(new Set(files.map(f => f.id)));
        }
    };

    const handleFileSelect = (fileId: string) => {
        const newSelected = new Set(selectedFiles);
        if (newSelected.has(fileId)) {
            newSelected.delete(fileId);
        } else {
            newSelected.add(fileId);
        }
        setSelectedFiles(newSelected);
    };

    const handleBulkAssociate = () => {
        if (selectedFiles.size === 0) return;
        setShowAssociateModal(true);
    };

    const confirmBulkAssociate = () => {
        const fileIds = Array.from(selectedFiles);
        const associated = documentService.bulkAssociateToContainer(fileIds, selectedContainerId);

        // Notifica il parent component
        associated.forEach(file => {
            onFileAssociate?.(file);
        });

        setSelectedFiles(new Set());
        setShowAssociateModal(false);
        setSelectedContainerId("");
    };


    const documents = files.map(f => ({
        id: f.id,
        containerId: "",
        type: f.type,
        name: f.name,
        uploadedAt: f.uploadedAt,
        extractedData: f.extractedData || {},
        missingFields: f.missingFields || [],
        status: f.status,
        fileUrl: f.fileUrl,
        fileSize: f.fileSize,
        mimeType: f.mimeType,
    }));

    return (
        <div className={className}>
            {/* Bulk Actions Bar */}
            {selectedFiles.size > 0 && (
                <div className="mb-4 flex items-center justify-between rounded-lg bg-primary p-3 ring-1 ring-secondary">
                    <div className="flex items-center gap-4">
                        <Checkbox
                            isSelected={selectedFiles.size === files.length && files.length > 0}
                            onChange={handleSelectAll}
                        >
                            {selectedFiles.size === files.length
                                ? t("drive.unmapped.bulkActions.deselectAll")
                                : t("drive.unmapped.bulkActions.selectAll")}
                        </Checkbox>
                        <span className="text-sm text-tertiary">
                            {t("drive.unmapped.bulkActions.selected", { count: selectedFiles.size })}
                        </span>
                        <Button color="primary" size="sm" onClick={handleBulkAssociate}>
                            {t("drive.unmapped.bulkActions.associate")}
                        </Button>
                    </div>
                </div>
            )}

            {/* Files List */}
            {files.length === 0 ? (
                <div className="text-center py-12">
                    <p className="text-lg font-medium text-primary mb-2">
                        {t("drive.unmapped.empty.title")}
                    </p>
                    <p className="text-tertiary">{t("drive.unmapped.empty.message")}</p>
                </div>
            ) : (
                <DocumentList
                    documents={documents}
                    viewMode={viewMode}
                    selectedItems={Array.from(selectedFiles)}
                    onDocumentCheckboxChange={(doc) => {
                        handleFileSelect(doc.id);
                    }}
                />
            )}

            {/* Associate Modal */}
            {showAssociateModal && (
                <ModalOverlay isOpen={showAssociateModal} onOpenChange={setShowAssociateModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.unmapped.associate.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <Select
                                    label={t("drive.unmapped.associate.label")}
                                    selectedKey={selectedContainerId}
                                    onSelectionChange={(key) => setSelectedContainerId(key as string)}
                                    items={containerOptions}
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => setShowAssociateModal(false)}>
                                        {t("drive.unmapped.associate.cancel")}
                                    </Button>
                                    <Button
                                        onClick={confirmBulkAssociate}
                                        isDisabled={!selectedContainerId || selectedFiles.size === 0}
                                    >
                                        {t("drive.unmapped.associate.save")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};

